import { Editor, NodeViewRendererProps } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeViewWrapper } from '@tiptap/react';
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';

export interface ImageAttributes {
  src: string;
  alt?: string;
  title?: string;
  width: string;
  height: string;
  alignment: 'left' | 'center' | 'right' | 'none';
}

interface ImageResizeComponentProps extends NodeViewRendererProps {
  node: ProseMirrorNode & { attrs: ImageAttributes };
  updateAttributes: (attrs: Partial<ImageAttributes>) => void;
  editor: Editor;
}

const ImageResizeComponent: React.FC<ImageResizeComponentProps> = ({
  node,
  updateAttributes,
  editor,
}) => {
  const [showControls, setShowControls] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: node.attrs.width,
    height: node.attrs.height,
  });
  const [alignment, setAlignment] = useState(node.attrs.alignment);
  const [controlsPosition, setControlsPosition] = useState<'top' | 'bottom'>(
    'top'
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const isEditable = editor.isEditable;

  const updateNodeAttributes = () => {
    if (isEditable) {
      updateAttributes({
        width: dimensions.width,
        height: dimensions.height,
        alignment: alignment,
      });
    }
  };

  const handleResize = (e: React.MouseEvent, corner: string) => {
    if (!isEditable) return;

    e.preventDefault();
    const startX = e.clientX;
    const startWidth = containerRef.current?.offsetWidth || 0;

    const onMouseMove = (e: MouseEvent) => {
      const deltaX = corner.includes('right')
        ? e.clientX - startX
        : -(e.clientX - startX);
      const newWidth = startWidth + deltaX;
      setDimensions((prev) => ({ ...prev, width: `${newWidth}px` }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      updateNodeAttributes();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleAlign = (newAlignment: ImageAttributes['alignment']) => {
    if (!isEditable) return;

    setAlignment(newAlignment);
    updateNodeAttributes();
  };

  const updateControlsPosition = useCallback(() => {
    if (isEditable && showControls && containerRef.current) {
      const editorElement = editor.view.dom;
      if (!editorElement) return;

      const editorRect = editorElement.getBoundingClientRect();
      const imageRect = containerRef.current.getBoundingClientRect();
      const spaceAbove = imageRect.top - editorRect.top;
      const spaceBelow = editorRect.bottom - imageRect.bottom;

      const controlsHeight = 40;

      if (spaceAbove >= controlsHeight || spaceAbove > spaceBelow) {
        setControlsPosition('top');
      } else {
        setControlsPosition('bottom');
      }
    }
  }, [showControls, editor.view, isEditable]);

  useEffect(() => {
    if (!isEditable) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowControls(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditable]);

  useEffect(() => {
    if (!isEditable) return;

    updateControlsPosition();
    window.addEventListener('resize', updateControlsPosition);
    return () => window.removeEventListener('resize', updateControlsPosition);
  }, [showControls, updateControlsPosition, isEditable]);

  useEffect(() => {
    if (isEditable && editor) {
      editor.on('update', updateControlsPosition);
    }
  }, [editor, updateControlsPosition, isEditable]);

  const containerStyle: React.CSSProperties = {
    width: dimensions.width,
    height: dimensions.height,
  };

  const containerClasses = cn(
    'relative',
    isEditable && 'cursor-move',
    showControls && isEditable && 'border border-dashed border-gray-400',
    {
      'mx-auto': alignment === 'center',
      'ml-auto': alignment === 'right',
      'mr-auto': alignment === 'left',
    }
  );

  const controlsClasses = cn(
    'absolute left-1/2 transform -translate-x-1/2 flex bg-background border border-gray-300 rounded-md p-0.5 z-50',
    controlsPosition === 'top' ? '-top-10' : '-bottom-10'
  );

  return (
    <NodeViewWrapper
      className="image-component-wrapper"
      data-drag-handle={isEditable}
    >
      <div
        ref={containerRef}
        style={containerStyle}
        className={containerClasses}
        onClick={() => isEditable && setShowControls(true)}
      >
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt}
          className="h-full w-full object-contain"
        />
        {isEditable && showControls && (
          <>
            <div className={controlsClasses}>
              <button
                onClick={() => handleAlign('left')}
                className="rounded p-1 hover:bg-gray-100"
              >
                <AlignLeft className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => handleAlign('center')}
                className="rounded p-1 hover:bg-gray-100"
              >
                <AlignCenter className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => handleAlign('right')}
                className="rounded p-1 hover:bg-gray-100"
              >
                <AlignRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div
              className="absolute -left-1 -top-1 h-2 w-2 cursor-nwse-resize rounded-full border border-gray-400 bg-background"
              onMouseDown={(e) => handleResize(e, 'top-left')}
            />
            <div
              className="absolute -right-1 -top-1 h-2 w-2 cursor-nesw-resize rounded-full border border-gray-400 bg-background"
              onMouseDown={(e) => handleResize(e, 'top-right')}
            />
            <div
              className="absolute -bottom-1 -left-1 h-2 w-2 cursor-nesw-resize rounded-full border border-gray-400 bg-background"
              onMouseDown={(e) => handleResize(e, 'bottom-left')}
            />
            <div
              className="absolute -bottom-1 -right-1 h-2 w-2 cursor-nwse-resize rounded-full border border-gray-400 bg-background"
              onMouseDown={(e) => handleResize(e, 'bottom-right')}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default ImageResizeComponent;
